/**
 * Express wallet detection and rendering (WP-19, PRD §1.5.1).
 *
 * This module only asks the device whether it can pay and renders the resulting button. It
 * never decides whether the *fund* may skip the identity step — that is
 * `Rest\WalletEligibility::is_eligible()`, computed server-side and delivered as
 * `form.wallets_eligible` (`05-API-CONTRACT.md` §5.4). `wizard.js` is the only caller, and it
 * consumes that flag rather than re-deriving it from the field list, per `07-CODE-STANDARDS.md`'s
 * seam-consumption rule — the exact mistake this project's own history records for
 * `coverage_offerable()` (WP-10 → WP-17 → fixed WP-18).
 *
 * Stripe's `PaymentRequest` is the only wallet mechanism Phase 4 wires up — `form.wallet.gateway`
 * is `'stripe'` or the config is absent entirely (`Rest\FormSchema::wallet_configuration()`).
 */

import { loadGateway } from './gateway-loader.js';
import { stripeElementsOptions } from '../shared/brand.js';

/**
 * Builds one wallet button controller, or `null` when there is nothing to offer — no gateway
 * configured, the SDK failed to load, or the device itself cannot pay. Every one of those is the
 * same outcome to the caller: render nothing, reserve no space (PRD §1.5.1: "graceful absence, no
 * layout shift, no empty slot").
 *
 * Detection only. Nothing here charges anything or reaches the checkout REST routes — the
 * `paymentmethod` callback is the caller's, and this module returns the moment the device's own
 * capability check resolves.
 *
 * @param {{gateway?: string, public_config?: {publishable_key?: string}}} walletConfig `form.wallet`.
 * @param {string} currency ISO 4217 currency code, in the case `Money::DEFAULT_CURRENCY` returns it.
 * @param {(event: any) => void} onPaymentMethod Called once the donor confirms with their device.
 * @return {Promise<{mount: (el: HTMLElement) => void, update: (amountMinorUnits: number, label: string) => void, stripe: any}|null>}
 */
export async function createWalletController( walletConfig, currency, onPaymentMethod ) {
	if ( ! walletConfig || 'stripe' !== walletConfig.gateway ) {
		return null;
	}

	const publishableKey = walletConfig.public_config && walletConfig.public_config.publishable_key;

	// The organisation's own country, from the server (rule 7); no country, no wallet.
	if ( ! publishableKey || ! walletConfig.country ) {
		return null;
	}

	const StripeFactory = await loadGateway( 'stripe' );

	if ( ! StripeFactory ) {
		return null;
	}

	const stripe = StripeFactory( publishableKey );

	/*
	 * `total` is a placeholder until the caller's first `update()`, from the same server-computed
	 * quote the summary bar already renders (A-1: the client never calculates). Stripe requires a
	 * non-negative integer here to construct the request at all; it is never what the donor is
	 * actually charged; the confirmed amount is whatever `POST /checkout/intent` returns.
	 */
	const paymentRequest = stripe.paymentRequest( {
		country: walletConfig.country,
		currency: ( currency || 'usd' ).toLowerCase(),
		total: { label: 'Donation', amount: 0 },
		requestPayerName: true,
		requestPayerEmail: true,
		// Not asked for: nothing sends or stores it (release gate 0.5.3, round 14, LOW-4).
		requestPayerPhone: false,
	} );

	let canPay = false;

	try {
		canPay = !! ( await paymentRequest.canMakePayment() );
	} catch ( error ) {
		canPay = false;
	}

	if ( ! canPay ) {
		return null;
	}

	/*
	 * Whether the device sheet is open. Its total must not change while it is: if Stripe applied or
	 * ignored an update then, `shownTotal` would name a figure the donor never saw (release gate
	 * 0.5.3, adversarial review round 11, LOW-3; Stripe documents `update()` only for a closed sheet).
	 */
	let sheetOpen = false;

	// Once the sheet closes, a total refused while it was open is applied (round 12, MEDIUM-4).
	const closed = () => {
		if ( 'function' === typeof controller.onSheetClosed ) {
			controller.onSheetClosed();
		}
	};

	paymentRequest.on( 'paymentmethod', ( event ) => {
		// Still open until the payment is completed or refused: the sheet shows its total until then.
		sheetOpen = true;

		/*
		 * A copy with its own `complete`, not a reassignment on Stripe's object, which may not be
		 * writable (round 12, LOW-4). The total is re-applied after the sheet has completed.
		 */
		onPaymentMethod(
			Object.assign( {}, event, {
				complete: ( status ) => {
					event.complete( status );

					// Only success surely closes it: after "fail" a sheet may stay open for another
					// try, still showing its total (round 13, MEDIUM-3). Cancel closes it too.
					if ( 'success' === status ) {
						sheetOpen = false;
						closed();
					}
				},
			} )
		);
	} );
	paymentRequest.on( 'cancel', () => {
		sheetOpen = false;
		closed();
	} );

	const elements = stripe.elements( stripeElementsOptions() );
	const button = elements.create( 'paymentRequestButton', { paymentRequest } );

	if ( 'function' === typeof button.on ) {
		button.on( 'click', () => {
			/*
			 * The moment before a sheet opens is the one Stripe allows an update in: the current
			 * total is applied here, so a sheet that closed without saying so cannot leave the
			 * wallet showing an old figure for ever (round 13, MEDIUM-3).
			 */
			sheetOpen = false;
			closed();
			sheetOpen = true;
		} );
	}

	const controller = {
		stripe,
		paymentRequest,

		/**
		 * The total the sheet last showed the donor, in minor units; null before the first
		 * quote. What the donor authorises, so what the charge must equal (round 9, MEDIUM-1).
		 *
		 * @type {number|null}
		 */
		shownTotal: null,

		/**
		 * Called when the sheet closes; the wizard sets it to re-apply its current total.
		 *
		 * @type {Function|null}
		 */
		onSheetClosed: null,

		/**
		 * @param {HTMLElement} el
		 * @return {void}
		 */
		mount( el ) {
			button.mount( el );
		},

		/**
		 * Keeps the wallet sheet's own total in sync with the server-computed quote. Never called
		 * with a client-derived figure — every caller passes through a value that already came
		 * from `POST /checkout/quote` or `POST /checkout/intent`.
		 *
		 * @param {number} amountMinorUnits
		 * @param {string} label
		 * @return {void}
		 */
		update( amountMinorUnits, label ) {
			if ( sheetOpen ) {
				return;
			}

			const amount = Math.max( 0, Math.round( amountMinorUnits ) );

			paymentRequest.update( {
				total: { label, amount },
			} );
			this.shownTotal = amount;
		},
	};

	return controller;
}

/**
 * Splits a wallet's single `name` field into first/last, the shape the checkout's own identity
 * fields use. Apple Pay and Google Pay both return one string; there is no reliable way to know
 * where a "first name" ends in every locale, so this is a display convenience only — the full
 * name is never lost, since `payer_name`-equivalent fields are not re-validated against this
 * split anywhere.
 *
 * @param {string} fullName
 * @return {{firstName: string, lastName: string}}
 */
export function splitPayerName( fullName ) {
	const trimmed = ( fullName || '' ).trim();

	if ( '' === trimmed ) {
		return { firstName: '', lastName: '' };
	}

	const parts = trimmed.split( /\s+/ );

	if ( 1 === parts.length ) {
		return { firstName: parts[ 0 ], lastName: '' };
	}

	return { firstName: parts[ 0 ], lastName: parts.slice( 1 ).join( ' ' ) };
}
